import { HttpClient, HttpContext, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map, combineLatest, Observable, switchMap } from "rxjs";
import { DefaultDocCat } from "./generated";
import { ConfigService } from "../config/config.service";
import {
  PatchedCollectionOfDocRef,
  PatchedDefaultDocRef,
} from "../models/patched-default-doc-ref";
import { parseApiDateHelper } from "./helpers/parse-api-date-helper";

@Injectable({
  providedIn: "root",
})
export class PiaDocumentsService {
  http = inject(HttpClient);
  configService = inject(ConfigService);

  getDocumentRefWithDocumentAndDocumentCategoriesExpansionByCountryYearAndProducts(
    {
      countryID,
      year,
      productIDs,
    }: {
      countryID: string;
      year: number;
      productIDs: string[];
    },
    context?: HttpContext,
  ): Observable<{ d?: PatchedCollectionOfDocRef }> {
    const headers = new HttpHeaders({
      accept: "application/json",
    });

    const docRefRequests = productIDs.map((productID) => {
      const url = `${this.configService.piaAPIOrigin}/DocRef?$filter=Year eq ${encodeURIComponent(year)} and Country eq '${encodeURIComponent(countryID.toUpperCase())}' and Product eq '${encodeURIComponent(productID)}'&$expand=Document,Document/Categories`;
      return this.http.get<{ d?: PatchedCollectionOfDocRef }>(url, {
        headers,
        context,
      });
    });

    const internationalDocRefRequests =
      countryID.toUpperCase() === "INT"
        ? []
        : productIDs.map((productID) => {
            const url = `${this.configService.piaAPIOrigin}/DocRef?$filter=Year eq ${encodeURIComponent(year)} and Country eq 'INT' and Product eq '${encodeURIComponent(productID)}'&$expand=Document,Document/Categories`;
            return this.http.get<{ d?: PatchedCollectionOfDocRef }>(url, {
              headers,
              context,
            });
          });

    return combineLatest([
      ...docRefRequests,
      ...internationalDocRefRequests,
    ]).pipe(
      map((collectionsOfDocRef: { d?: PatchedCollectionOfDocRef }[]) => {
        return {
          d: {
            results: Array.from(
              new Set([
                ...(collectionsOfDocRef
                  ?.map((collectionOfDocRef) => collectionOfDocRef?.d?.results)
                  .flat() ?? []),
              ]),
            ),
          },
        } as { d?: PatchedCollectionOfDocRef };
      }),
    );
  }

  getDocumentsByCountryLanguageYearProductAndCategory(
    {
      countryID,
      year,
      productIDs,
      docCatID,
      includedLanguages,
    }: {
      countryID: string;
      year: number;
      productIDs: string[];
      docCatID: string;
      includedLanguages: string[];
    },
    context?: HttpContext,
  ) {
    return this.getDocumentsByCountryLanguageYearAndProduct(
      {
        countryID,
        year,
        productIDs,
        includedLanguages,
      },
      context,
    ).pipe(
      map((docRefs) =>
        docRefs.filter((docRef) => {
          // filter out results that do not belong to current category
          return (
            docRef.Document?.Categories?.results?.[0]?.CategoryId?.toLowerCase() ===
            docCatID.toLowerCase()
          );
        }),
      ),
    );
  }

  getDocumentsByCountryLanguageYearAndProduct(
    {
      countryID,
      year,
      productIDs,
      includedLanguages,
    }: {
      countryID: string;
      year: number;
      productIDs: string[];
      includedLanguages: string[];
    },
    context?: HttpContext,
  ) {
    const response =
      this.getDocumentRefWithDocumentAndDocumentCategoriesExpansionByCountryYearAndProducts(
        {
          countryID,
          year,
          productIDs,
        },
        context,
      );

    const documentIdsIncluded = new Set<string>();

    return response.pipe(
      map((documentReferences: { d?: PatchedCollectionOfDocRef }) => {
        return !documentReferences.d?.results
          ? []
          : documentReferences.d.results
              .filter((documentReference: PatchedDefaultDocRef) => {
                if (!documentReference.DocumentId) {
                  return false;
                }
                const countryMatches =
                  documentReference?.Country &&
                  // use current country and int (international) document
                  (documentReference?.Country.toLowerCase() ===
                    countryID.toLowerCase() ||
                    documentReference?.Country.toLowerCase() === "int");
                const documentTypeMatches =
                  documentReference.Document?.Type &&
                  // only documents in this allow list are considered documents, thus filter out everything else
                  this.configService.piaProductDocumentTypes.includes(
                    documentReference.Document.Type,
                  );
                const documentLanguageIncluded =
                  documentReference.Document?.Language &&
                  includedLanguages.includes(
                    documentReference.Document?.Language,
                  );

                const isDuplicate = documentIdsIncluded.has(
                  documentReference.DocumentId,
                );
                if (
                  !isDuplicate &&
                  countryMatches &&
                  documentTypeMatches &&
                  documentLanguageIncluded
                ) {
                  documentIdsIncluded.add(documentReference.DocumentId);
                  return true;
                }
                return false;
              })
              .sort((a: PatchedDefaultDocRef, b: PatchedDefaultDocRef) => {
                const yearA = a.Document?.Year ?? 0;
                const yearB = b.Document?.Year ?? 0;

                if (yearA !== yearB) {
                  return yearA - yearB;
                }

                const modA = parseApiDateHelper(a.Document?.Modified);
                const modB = parseApiDateHelper(b.Document?.Modified);

                return modA - modB;
              });
      }),
    );
  }

  getUniqueDocCatsByLanguage({
    countryID,
    languageID,
    year,
    productIDs,
    includedLanguages,
  }: {
    countryID: string;
    languageID: string;
    year: number;
    productIDs: string[];
    includedLanguages: string[];
  }): Observable<DefaultDocCat[]> {
    return this.getDocumentCategoriesWithDocuments({
      countryID,
      year,
      productIDs,
      includedLanguages,
    }).pipe(
      map((response) => {
        const filtered =
          response?.filter(
            (item) =>
              item.Language &&
              item.Language?.toLowerCase() === languageID.toLowerCase() &&
              item.Title,
          ) ?? [];
        const sorted = filtered.sort((a, b) => {
          const sortA = typeof a.Sort === "number" ? a.Sort : 0;
          const sortB = typeof b.Sort === "number" ? b.Sort : 0;
          return sortA - sortB;
        });
        return [...new Set(sorted)];
      }),
    );
  }

  getAllDocumentCategories() {
    const url = `${this.configService.piaAPIOrigin}/DocCat`;
    const headers = new HttpHeaders({
      accept: "application/json",
    });
    return this.http.get<{ d: { results: DefaultDocCat[] } }>(url, { headers });
  }

  getAvailableLanguages(): Observable<string[] | undefined> {
    return this.getAllDocumentCategories().pipe(
      map((response) => [
        ...new Set(
          response?.d?.results?.length
            ? response.d.results
                .map((item) => item.Language)
                .filter(
                  (lang): lang is string => typeof lang === "string" && !!lang,
                )
            : [],
        ),
      ]),
    );
  }

  /**
   * Returns document categories that are present in the documents returned by getDocumentsByCountryLanguageYearAndProduct.
   * Only categories whose ID matches the first category of any document are included.
   */
  getDocumentCategoriesWithDocuments({
    countryID,
    year,
    productIDs,
    includedLanguages,
  }: {
    countryID: string;
    year: number;
    productIDs: string[];
    includedLanguages: string[];
  }): Observable<DefaultDocCat[]> {
    return this.getDocumentsByCountryLanguageYearAndProduct({
      countryID,
      year,
      productIDs,
      includedLanguages,
    }).pipe(
      map((docRefs) => {
        // Collect all unique category IDs from the first category of each document
        const categoryIds = new Set<string>();
        docRefs.forEach((docRef) => {
          const cat = docRef.Document?.Categories?.results?.[0];
          if (typeof cat?.CategoryId === "string") {
            categoryIds.add(cat.CategoryId);
          }
        });
        return categoryIds;
      }),
      //  Use switchMap to wait for categoryIds before filtering categories
      switchMap((categoryIds: Set<string>) =>
        this.getAllDocumentCategories().pipe(
          map((response) =>
            response.d.results.filter(
              (cat) => typeof cat.ID === "string" && categoryIds.has(cat.ID),
            ),
          ),
        ),
      ),
    );
  }
}
